import React, { useState } from "react";
import FullScreenSpinner from "../../components/FullScreenSpinner";
import {
  Box, Typography, Stack, Button, IconButton, Grid,
  Divider, FormControl, Select, MenuItem, Checkbox,
  ListItemText, TextField, Chip, List, ListItem, ListItemButton,
  ListSubheader, InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import useIndGobData from "../../hooks/indicadorGubernamental/useIndGobData";
import SearchableSelect from "../../components/Common/SearchableSelect";

import {
  CardPaper,
  ContentInner,
  GobShell,
  LegendDot,
  LeftPanel,
  MainHeader,
  MainHeaderLeft,
  MainHeaderRight,
  MainPanel,
  MainScroll,
  RightPanel,
  ChevronCell,
  BarRow,
  BarTrack,
  BarSegment,
  BarLabel,
  BarValor,
  BarTitleRow,
  HeatWrap,
  HeatTable,
  HeatTh,
  HeatTd,
} from "./IndicadorGubernamental.styles";

const colorForPct = (pct) => {
  const v = Number(pct);
  if (Number.isNaN(v)) return { bg: "#f8fafc", text: "#94a3b8" };
  if (v >= 0.8) return { bg: "#dcfce7", text: "#15803d" }; // Verde suave / pastel
  if (v >= 0.5) return { bg: "#fef9c3", text: "#854d0e" }; // Amarillo suave / ámbar
  return { bg: "#fee2e2", text: "#991b1b" }; // Rojo suave / pastel
};

const BAR_COLOR_DASHBOARD = "#1054ad";

const fmtPct = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "0%";
  return `${(n * 100).toFixed(1)}%`;
};

const fmtRealizado = (r, t) => {
  const rn = Number(r) || 0;
  const tn = Number(t) || 0;
  const pct = tn > 0 ? rn / tn : 0;
  return `${rn.toLocaleString()} / ${tn.toLocaleString()} (${fmtPct(pct)})`;
};

const IndicadorGubernamental = () => {
  const [isLeftOpen, setIsLeftOpen] = useState(true);
  const [isRightOpen, setIsRightOpen] = useState(true);
  const [searchTermFiltros, setSearchTermFiltros] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});

  const {
    config,
    activeTab,
    setActiveTab,
    loadingConfig,
    filtrosValores,
    catalogos,
    loadingIndicadores,
    seccionInfo,
    datasetActivo,
    handleFiltroChange,
    aplicarFiltros,
    limpiarFiltros,
  } = useIndGobData();

  if (loadingConfig) return <FullScreenSpinner text="Cargando configuración..." />;

  if (!config || config.length === 0) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh", fontFamily: "var(--font-body)" }}>
        <Typography variant="h6" color="text.secondary">No se encontró la configuración de Indicador Gubernamental.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>Reintentar</Button>
      </Box>
    );
  }

  const toggleNode = (id) => {
    setExpandedNodes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderBarraNodo = (node, nivel, parentExpanded) => {
    if (!node) return null;
    if (parentExpanded === false && nivel > 0) return null;
    const id = node._id;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = nivel === 0 ? true : !!expandedNodes[id];
    const pctRaw = node.cumplimiento;
    const pct = typeof pctRaw === 'number'
      ? pctRaw
      : (Number(node.total || 0) > 0 ? Number(node.realizado || 0) / Number(node.total || 0) : 0);
    const widthPct = Math.max(0, Math.min(100, pct * 100));
    const visible = nivel === 0 || parentExpanded;
    if (!visible && nivel > 0) return null;

    return (
      <React.Fragment key={id}>
        <BarRow
          $nivel={nivel}
          $clickable={hasChildren}
          onClick={() => hasChildren && nivel > 0 && toggleNode(id)}
        >
          <ChevronCell $visible={hasChildren}>
            {nivel === 0 ? "•" : (isExpanded ? "−" : "+")}
          </ChevronCell>
          <BarLabel title={String(node.nombre || "")}>{node.nombre || "—"}</BarLabel>
          <BarTrack>
            <BarSegment $bg={BAR_COLOR_DASHBOARD} style={{ width: `${widthPct}%` }} />
          </BarTrack>
          <BarValor>{fmtRealizado(node.realizado, node.total)}</BarValor>
        </BarRow>
        {hasChildren && node.children.map((ch) => renderBarraNodo(ch, nivel + 1, isExpanded))}
      </React.Fragment>
    );
  };

  const renderJerarquia = () => {
    if (!datasetActivo || datasetActivo.tipo !== 'barra') return null;
    const { roots } = datasetActivo;
    return (
      <CardPaper elevation={0} sx={{ p: 2.5, flex: 1, bgcolor: "#fff", borderRadius: "12px", border: "1px solid #edf2f7", overflowY: "auto" }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              lineHeight: 1.2,
              color: "#0f172a",
              fontFamily: "var(--font-display)"
            }}
          >
            {seccionInfo?.nombre || "Indicador Gubernamental"}
          </Typography>
        </Stack>

        <BarTitleRow sx={{ padding: "6px 0 10px" }}>
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, fontFamily: "var(--font-body)", pl: 4, fontSize: "10px" }}>
            DESGLOSE
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, fontFamily: "var(--font-body)", width: 130, textAlign: "right", fontSize: "10px" }}>
            REALIZADOS / TOTAL
          </Typography>
        </BarTitleRow>

        {roots.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center", color: "#94a3b8", fontFamily: "var(--font-body)" }}>
            <Typography variant="body2">Sin datos para los filtros seleccionados.</Typography>
          </Box>
        ) : (
          roots.map((r) => renderBarraNodo(r, 0, true))
        )}
      </CardPaper>
    );
  };

  const renderMatriz = () => {
    if (!datasetActivo || datasetActivo.tipo !== 'matriz') return null;
    const { actividades, cols, grid, totalsRow, totalsCol, totalGlobal } = datasetActivo;

    const cell = (actKey, colKey) => grid.get(`${actKey}__${colKey}`) || { total: 0, realizado: 0, valor: null };

    return (
      <CardPaper elevation={0} sx={{ p: 2, flex: 1, bgcolor: "#fff", borderRadius: "12px", border: "1px solid #edf2f7" }}>
        <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} alignItems="center" flexWrap="wrap">
          <Typography
            sx={{
              fontSize: "13.5px",
              fontWeight: 700,
              lineHeight: 1.2,
              color: "#0f172a",
              fontFamily: "var(--font-display)"
            }}
          >
            {seccionInfo?.nombre || "Matriz de Cumplimiento"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto" }}>
            <Stack direction="row" spacing={1.5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LegendDot $color="#22c55e" style={{ background: "#dcfce7", border: "1px solid #86efac" }} />
                <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 700, fontFamily: "var(--font-body)", fontSize: "10px" }}>≥ 80%</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LegendDot $color="#ca8a04" style={{ background: "#fef9c3", border: "1px solid #fde047" }} />
                <Typography variant="caption" sx={{ color: "#854d0e", fontWeight: 700, fontFamily: "var(--font-body)", fontSize: "10px" }}>50% - 79%</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LegendDot $color="#ef4444" style={{ background: "#fee2e2", border: "1px solid #fca5a5" }} />
                <Typography variant="caption" sx={{ color: "#991b1b", fontWeight: 700, fontFamily: "var(--font-body)", fontSize: "10px" }}>{`< 50%`}</Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>

        <HeatWrap>
          <HeatTable>
            <thead>
              <tr>
                <HeatTh $align="left" $firstCol style={{ left: 0, minWidth: 165, maxWidth: 180, width: 170 }}>
                  <span style={{ fontSize: "9.5px", fontWeight: 750, color: "#1e293b", textTransform: "uppercase" }}>
                    Actividad Trazadora
                  </span>
                </HeatTh>
                {cols.map((c) => {
                  const isTotal = c.key === '__TOTAL__';
                  return (
                    <HeatTh
                      key={c.key}
                      $align="center"
                      $bold={isTotal}
                      title={c.label}
                      style={{
                        minWidth: isTotal ? 65 : 85,
                        maxWidth: isTotal ? 75 : 110,
                        width: isTotal ? 70 : 95,
                        padding: "4px 4px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textAlign: "center",
                          fontSize: isTotal ? "9px" : "8px",
                          fontWeight: 700,
                          lineHeight: 1.15,
                          textTransform: "uppercase",
                          color: isTotal ? "#0f172a" : "#334155",
                          letterSpacing: "0.01em",
                        }}
                      >
                        {c.label}
                      </Box>
                    </HeatTh>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {actividades.length === 0 && (
                <tr>
                  <HeatTd $firstCol colSpan={(cols.length || 0) + 1} $align="center" style={{ color: "#94a3b8", padding: "16px 0" }}>
                    Sin datos para los filtros seleccionados.
                  </HeatTd>
                </tr>
              )}
              {actividades.map((a) => {
                return (
                  <tr key={a.key}>
                    <HeatTd
                      $firstCol
                      $align="left"
                      $wrap
                      style={{
                        minWidth: 165,
                        maxWidth: 180,
                        width: 170,
                        fontSize: "9px",
                        fontWeight: 600,
                        color: "#1e293b",
                        lineHeight: 1.15,
                        display: "table-cell",
                        padding: "3px 5px",
                      }}
                      title={String(a.label || "")}
                    >
                      <Box
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {a.label}
                      </Box>
                    </HeatTd>
                    {cols.map((c) => {
                      const g = cell(a.key, c.key);
                      const pct = typeof g.valor === 'number' ? g.valor : (g.total > 0 ? g.realizado / g.total : null);
                      const isTotalCol = c.key === '__TOTAL__';
                      const colors = colorForPct(pct);
                      if (g.total === 0 && !isTotalCol) {
                        return (
                          <HeatTd
                            key={c.key}
                            $align="center"
                            style={{
                              color: "#cbd5e1",
                              minWidth: isTotalCol ? 65 : 85,
                              maxWidth: isTotalCol ? 75 : 110,
                              width: isTotalCol ? 70 : 95,
                              padding: "3px 4px",
                            }}
                          >
                            —
                          </HeatTd>
                        );
                      }
                      return (
                        <HeatTd
                          key={c.key}
                          $align="center"
                          $bg={colors.bg}
                          $color={colors.text}
                          $bold={isTotalCol}
                          style={{
                            minWidth: isTotalCol ? 65 : 85,
                            maxWidth: isTotalCol ? 75 : 110,
                            width: isTotalCol ? 70 : 95,
                            padding: "3px 4px",
                            fontWeight: 600,
                          }}
                          title={g.total > 0 ? `${g.realizado.toLocaleString()} / ${g.total.toLocaleString()} = ${fmtPct(pct)}` : "—"}
                        >
                          {pct == null ? "—" : fmtPct(pct)}
                        </HeatTd>
                      );
                    })}
                  </tr>
                );
              })}
              {totalsRow && (
                <tr>
                  <HeatTd $firstCol $align="left" $bold style={{ background: "#f1f5f9", minWidth: 165, maxWidth: 180, width: 170, fontSize: "9px" }}>
                    {totalsRow.label || "TOTAL"}
                  </HeatTd>
                  {cols.map((c) => {
                    const g = cell(totalsRow.key, c.key);
                    const fromTotalsCol = totalsCol.get(c.key);
                    const src = (g && g.total > 0) ? g : fromTotalsCol;
                    const pct = src ? (typeof src.valor === 'number' ? src.valor : (src.total > 0 ? src.realizado / src.total : null)) : null;
                    const isTotalCol = c.key === '__TOTAL__';
                    const gFinal = totalGlobal && isTotalCol ? totalGlobal : src;
                    const colors = colorForPct(pct);
                    if (!gFinal || gFinal.total === 0) {
                      return (
                        <HeatTd
                          key={c.key}
                          $align="center"
                          style={{
                            background: "#f1f5f9",
                            color: "#cbd5e1",
                            minWidth: isTotalCol ? 65 : 85,
                            maxWidth: isTotalCol ? 75 : 110,
                            width: isTotalCol ? 70 : 95,
                            padding: "3px 4px",
                          }}
                        >
                          —
                        </HeatTd>
                      );
                    }
                    return (
                      <HeatTd
                        key={c.key}
                        $align="center"
                        $bg={colors.bg}
                        $color={colors.text}
                        $bold
                        style={{
                          minWidth: isTotalCol ? 65 : 85,
                          maxWidth: isTotalCol ? 75 : 110,
                          width: isTotalCol ? 70 : 95,
                          padding: "3px 4px",
                          fontWeight: 700,
                        }}
                      >
                        {fmtPct(pct)}
                      </HeatTd>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </HeatTable>
        </HeatWrap>

        <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #eef2f7", color: "#64748b", fontSize: 11, fontFamily: "var(--font-body)" }}>
          <strong>Leyenda:</strong> cada celda muestra el porcentaje de cumplimiento de la actividad trazadora (fila) por {seccionInfo?.clave_tecnica === 'gob_cumpl_eps' ? 'EPS' : 'Municipio'} (columna). Última fila y última columna son promedios globales.
        </Box>
      </CardPaper>
    );
  };

  const renderFiltroField = (f) => {
    const catalogData = catalogos[f.id] || [];
    const keyVal = (f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin') ? f.nombre_campo : f.clave_vinculacion;
    const isMultiple = f.tipo_control === 'select_multiple';

    return (
      <Box key={f.id} sx={{ mb: 1.5 }}>
        <Typography component="label" sx={{ display: "block", fontSize: "11px", color: "#64748b", fontWeight: "500", mb: "3px", fontFamily: "var(--font-body)" }}>
          {f.label}
        </Typography>
        <FormControl fullWidth size="small">
          {f.tipo_control && f.tipo_control.startsWith('select') ? (
            <SearchableSelect
              multiple={isMultiple}
              options={catalogData}
              value={filtrosValores[keyVal] ?? (isMultiple ? [] : "")}
              onValueChange={(newVal) => handleFiltroChange(keyVal, newVal)}
              placeholder="Seleccione..."
              getOptionValue={(item) => String(item.id ?? item.eps ?? item.codigo ?? "")}
              getOptionLabel={(item) => String(item.descripcion || item.eps || item.nombre || item.label || item.codigo || "")}
              sx={{
                bgcolor: "#fff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  fontSize: "11px",
                  minHeight: "32px",
                  height: "32px",
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
              }}
              selectSx={{
                height: "32px",
                minHeight: "32px",
                fontSize: "11px",
                fontFamily: "var(--font-body)",
                "& .MuiSelect-select": {
                  padding: "4px 8px !important",
                  fontSize: "11px",
                  fontFamily: "var(--font-body)",
                },
              }}
            />
          ) : (
            <TextField
              fullWidth
              placeholder={f.nombre_campo && f.nombre_campo.includes('edad') ? "Ej: 18" : "Escriba..."}
              type={f.tipo_control || "text"}
              size="small"
              value={filtrosValores[(f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin') ? f.nombre_campo : f.clave_vinculacion] || ""}
              onChange={(e) => handleFiltroChange((f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin') ? f.nombre_campo : f.clave_vinculacion, e.target.value)}
              sx={{
                bgcolor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: "11px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                "& .MuiInputBase-input": { padding: "4px 8px !important" }
              }}
            />
          )}
        </FormControl>
      </Box>
    );
  };

  return (
    <GobShell $leftOpen={isLeftOpen} $rightOpen={isRightOpen}>
      {isLeftOpen && (
        <LeftPanel>
          <Box sx={{ p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#2d3748", fontSize: "13px", fontFamily: "var(--font-display)" }}>
              Secciones
            </Typography>
            <IconButton size="small" onClick={() => setIsLeftOpen(false)}>
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Divider />
          <List dense sx={{ mt: 0.5 }}>
            {config.map((s) => (
              <ListItem key={s.clave_tecnica} disablePadding>
                <ListItemButton
                  selected={activeTab === s.clave_tecnica}
                  onClick={() => setActiveTab(s.clave_tecnica)}
                  sx={{
                    py: 0.8,
                    px: 1.5,
                    mx: 0.5,
                    borderRadius: "6px",
                    mb: 0.3,
                    minHeight: 0,
                    "&.Mui-selected": { bgcolor: "rgba(26, 115, 232, 0.08)", color: "primary.main" },
                  }}
                >
                  <ListItemText
                    primary={s.nombre}
                    primaryTypographyProps={{
                      fontSize: "12px",
                      fontWeight: activeTab === s.clave_tecnica ? 700 : 500,
                      noWrap: true,
                      fontFamily: "var(--font-body)",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </LeftPanel>
      )}

      <MainPanel>
        <MainHeader $hidden={isLeftOpen && isRightOpen}>
          <MainHeaderLeft>
            {!isLeftOpen && (
              <IconButton size="small" onClick={() => setIsLeftOpen(true)}>
                <MenuIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </MainHeaderLeft>
          <MainHeaderRight>
            {!isRightOpen && (
              <IconButton size="small" onClick={() => setIsRightOpen(true)}>
                <FilterListIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </MainHeaderRight>
        </MainHeader>

        <MainScroll>
          {loadingIndicadores ? (
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <FullScreenSpinner />
              <Typography sx={{ color: "#A3AED0", fontWeight: 600, fontFamily: "var(--font-body)" }}>
                Sincronizando datos en tiempo real...
              </Typography>
            </Box>
          ) : (
            <ContentInner>
              {datasetActivo?.tipo === 'barra' && renderJerarquia()}
              {datasetActivo?.tipo === 'matriz' && renderMatriz()}
              {!datasetActivo && (
                <CardPaper elevation={0} sx={{ p: 6, textAlign: "center", color: "#94a3b8" }}>
                  <Typography variant="body2" sx={{ fontFamily: "var(--font-body)" }}>
                    Selecciona una sección para ver los indicadores gubernamentales.
                  </Typography>
                </CardPaper>
              )}
            </ContentInner>
          )}
        </MainScroll>
      </MainPanel>

      {isRightOpen && (
        <RightPanel>
          <Box sx={{ p: 1, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" sx={{ fontSize: "12px", fontWeight: "600", color: "#334155", fontFamily: "var(--font-display)" }}>
              Filtros
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: "5px",
                  textTransform: "none",
                  fontSize: "11px",
                  height: 26,
                  px: 1.5,
                  color: "#64748b",
                  borderColor: "#cbd5e1",
                  fontWeight: "600",
                  bgcolor: "#f1f5f9",
                  "&:hover": { bgcolor: "#e2e8f0", borderColor: "#94a3b8" },
                }}
                onClick={limpiarFiltros}
              >
                Limpiar
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{
                  borderRadius: "5px",
                  textTransform: "none",
                  fontSize: "11px",
                  height: 26,
                  px: 1.5,
                  bgcolor: "#22c55e",
                  fontWeight: "700",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#16a34a", boxShadow: "none" },
                }}
                onClick={aplicarFiltros}
              >
                Aplicar
              </Button>
              <IconButton size="small" onClick={() => setIsRightOpen(false)}>
                <ChevronLeftIcon sx={{ transform: "rotate(180deg)", fontSize: "16px" }} />
              </IconButton>
            </Stack>
          </Box>
          <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
            {seccionInfo?.filtros.reduce((acc, f, i, arr) => {
              if (f.nombre_campo === "edadIni") {
                const next = arr[i + 1];
                if (next && next.nombre_campo === "edadFin") {
                  acc.push(
                    <Grid container spacing={1} key="row-edades" sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <Typography component="label" sx={{ display: "block", fontSize: "10px", color: "#64748b", fontWeight: "500", mb: "2px", fontFamily: "var(--font-body)" }}>
                          Edad Min
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Ej: 18"
                          type="number"
                          size="small"
                          value={filtrosValores[f.nombre_campo] || ""}
                          onChange={(e) => handleFiltroChange(f.nombre_campo, e.target.value)}
                          sx={{
                            bgcolor: "#fff",
                            "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: "11px" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                            "& .MuiInputBase-input": { padding: "4px 6px !important" }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography component="label" sx={{ display: "block", fontSize: "10px", color: "#64748b", fontWeight: "500", mb: "2px", fontFamily: "var(--font-body)" }}>
                          Edad Max
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Ej: 65"
                          type="number"
                          size="small"
                          value={filtrosValores[next.nombre_campo] || ""}
                          onChange={(e) => handleFiltroChange(next.nombre_campo, e.target.value)}
                          sx={{
                            bgcolor: "#fff",
                            "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: "11px" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                            "& .MuiInputBase-input": { padding: "4px 6px !important" }
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                  return acc;
                }
              }
              if (f.nombre_campo === "edadFin") return acc;
              acc.push(renderFiltroField(f));
              return acc;
            }, [])}
          </Box>
        </RightPanel>
      )}
    </GobShell>
  );
};

export default IndicadorGubernamental;
